import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import type { Branch, BranchCreate, BranchUpdate } from '@shared/models/branch';
import type {
  DeleteResponse,
  PaginatedRequest,
  PaginatedResponse,
} from '@shared/models/core';
import { CACHE_TOKEN, UNCACHE_TOKEN } from '../cache';
import { map } from 'rxjs';
import { PROGRESS_TOKEN } from '../progress';
import { USER_REFRESH_TOKEN } from '../auth/user-refresh-interceptor';

const progressContext = new HttpContext().set(PROGRESS_TOKEN, {
  section: 'branch',
});

@Injectable({
  providedIn: 'root',
})
export class BranchApi {
  readonly #baseUrl = environment.apiUrl;
  readonly #apiUrl = `${this.#baseUrl}/organisation`;
  readonly #http = inject(HttpClient);

  getBranches(request: PaginatedRequest<Branch> & { name?: string } = {}) {
    const url = `${this.#baseUrl}/shops`;

    return this.#http.get<PaginatedResponse<Branch>>(url, {
      params: request,
      context: new HttpContext().set(CACHE_TOKEN, 'branches'),
    });
  }

  getBranchesByOrgId(orgId: string, request: PaginatedRequest<Branch> = {}) {
    return this.#http.get<PaginatedResponse<Branch>>(
      `${this.#apiUrl}/${orgId}/shop`,
      {
        params: request,
        context: new HttpContext().set(CACHE_TOKEN, 'branches'),
      },
    );
  }

  createBranch(orgId: string, branch: BranchCreate) {
    return this.#http.post<Branch>(
      `${this.#apiUrl}/${orgId}/shop`,
      {
        ...branch,
        address: JSON.stringify(branch.address),
      },
      {
        context: progressContext
          .set(USER_REFRESH_TOKEN, true)
          .set(UNCACHE_TOKEN, ['branches']),
      },
    );
  }

  getBranch(orgId: string, branchId: string) {
    return this.#http
      .get<Branch>(`${this.#apiUrl}/${orgId}/shop/${branchId}`, {
        context: new HttpContext().set(CACHE_TOKEN, 'branches'),
      })
      .pipe(
        // fix for the v2 api. the id is not returned in the response.
        map((res) => ({ ...res, id: branchId })),
      );
  }

  updateBranch(orgId: string, branchId: string, branch: BranchUpdate) {
    return this.#http.put<Branch>(
      `${this.#apiUrl}/${orgId}/shop/${branchId}`,
      {
        ...branch,
        address: branch.address ? JSON.stringify(branch.address) : undefined,
      },
      {
        context: progressContext.set(UNCACHE_TOKEN, ['branches']),
      },
    );
  }

  deleteBranch(orgId: string, branchId: string) {
    return this.#http.delete<DeleteResponse>(
      `${this.#apiUrl}/${orgId}/shop/${branchId}`,
      {
        context: progressContext
          .set(USER_REFRESH_TOKEN, true)
          .set(UNCACHE_TOKEN, ['branches']),
      },
    );
  }

  generateQRCode(orgId: string, branchId: string) {
    return this.#http.post<{
      message: string;
      qrCodeUrl: string;
      shopId: string;
    }>(
      `${this.#apiUrl}/${orgId}/shop/${branchId}/qr-code`,
      {},
      {
        context: progressContext.set(UNCACHE_TOKEN, ['branches']),
      },
    );
  }
}
